package ai.jessie.web.pages.organisations

import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.organisationRequiredProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.settings.LocalSettingsPort
import ai.jessie.web.settings.SettingsState
import ai.jessie.web.settings.SettingsUiState
import ai.jessie.web.settings.organisationSettings
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.settingsSideGroups
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div

// UC-025. Tenant state is keyed by the routed organisation, so a change of organisation discards
// it together with any unsaved proposal.
@Page("{organisationId}/settings")
@Composable
fun organisationSettingsPage(ctx: PageContext) {
    val organisationId = ctx.route.dynamicParams["organisationId"].orEmpty()
    if (!validOrganisationId(organisationId)) {
        appFrame(
            organisationId,
            settingsHref(organisationId),
            SETTINGS_TITLE,
            sideGroups = settingsSideGroups(organisationId, null),
        ) {
            Div(attrs = AlertStyle.toAttrs()) {
                onboardingProblem(organisationRequiredProblem(), busy = false)
            }
        }
    } else key(organisationId) { settingsContent(organisationId) }
}

@Composable
private fun settingsContent(organisationId: String) {
    val port = LocalSettingsPort.current
    val scope = rememberCoroutineScope()
    val model = remember(port, scope, organisationId) { SettingsState(port, scope, organisationId) }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val state = model.state
    appFrame(
        organisationId,
        settingsHref(organisationId),
        SETTINGS_TITLE,
        authenticationRequired = state.error?.authenticationRequired == true,
        sideGroups = settingsSideGroups(organisationId, state.current?.name),
        actions = settingsActions(state, model),
    ) {
        organisationSettings(organisationId, state, model.actions)
    }
}

// "Bearbeiten" opens the form only for the owner while the current settings are shown; the
// first configuration keeps its existing label.
private fun settingsActions(
    state: SettingsUiState,
    model: SettingsState,
): (@Composable () -> Unit)? {
    val current = state.current
    val viewing = state.draft == null && state.preview == null && !state.busy
    if (current?.editable != true || !viewing || state.error?.authenticationRequired == true)
        return null
    return {
        themedButton(
            if (current.configured) "Bearbeiten" else "Geschäftseinstellungen einrichten",
            model.actions.edit,
            variant = ButtonVariant.PRIMARY,
        )
    }
}

private fun settingsHref(organisationId: String) = "/organisations/$organisationId/settings"

private const val SETTINGS_TITLE = "Einstellungen"
